import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { CombinedError } from 'urql';
import { useWhiteLabelling } from 'components/WhiteLabellingProvider';
import useMarketInput, { DEFAULT_ADMIN_PROFILE_ID } from 'hooks/market/useMarketInput';
import { compareByRanks, isEqualString } from 'utils/compare';
import { isHeadless } from 'utils/headless';
import { isBuyerSelected, isMarketStateSet } from 'utils/isMarketStateSet';
import {
  CartCheckoutInput,
  CartGroup,
  CartInput,
  CartItem,
  CartItemInput,
  MarketMetric,
  MarketSellerInfo,
  NotificationType,
  Party,
  OrderInputType,
  Trucks,
  useBuyersMarketCartsQuery,
  useCartCheckoutMutation,
  useCartSetItemMutation,
  useNotifyMutation,
  useTrucksQuery,
  Cart,
  TruckType,
  SupplyType,
} from 'generated/graphql';
import { useAnalytics } from './useAnalytics';
import useDebouncedFunc from './useDebouncedFunc';
import { SnackbarVariant, useSnackbar } from './useSnackBar';
import useUser from './useUser';

const DEBOUNCE_TIME = 600;

/** Pending writes are keyed by cart item, so a refetch can't clobber what they hold. */
const cartItemKey = (sellerUUID: string, productUUID: string) => `${sellerUUID}:${productUUID}`;

/**
 * What a row needs that the mutation input doesn't carry, so an item can be shown before the
 * server has one. Only needed when adding a product the cart doesn't hold yet.
 */
export type PendingProduct = Pick<
  CartItem,
  'label' | 'images' | 'unitPrice' | 'casePack' | 'innerCasePack' | 'metricInfo'
> & { seller: MarketSellerInfo };

/** A write the server hasn't confirmed yet. */
type PendingWrite = {
  sellerUUID: string;
  /** The item as it was sent. A quantity of zero clears the row. */
  item: CartItemInput;
  product?: PendingProduct;
  /**
   * Where this write falls in the order the client made them. Carts asked for after it answer it,
   * whether or not its own request has come back yet.
   */
  seq: number;
};

type PendingItems = Record<string, PendingWrite | undefined>;

// Applies a pending CartItemInput to an item
const updateCartItem = (item: CartItem, pending: CartItemInput): CartItem => {
  const cases = item.casePack ? Math.ceil(pending.quantity / item.casePack) : item.cases;

  return {
    ...item,
    unitQuantity: pending.quantity,
    cases,
    ripeness: pending.ripeness,
    supply: pending.supply,
    metricInfo: item.metricInfo.map((info) => ({
      ...info,
      quantity: info.metric === MarketMetric.Pallets ? cases : pending.quantity,
    })),
  };
};

// Creates a new cart item with a given product
const createCartItem = ({ item, product }: { item: CartItemInput; product: PendingProduct }): CartItem => {
  const cases = product.casePack ? Math.ceil(item.quantity / product.casePack) : 0;

  return {
    productUUID: item.productUUID,
    ripeness: item.ripeness,
    supply: item.supply,
    unitQuantity: item.quantity,
    cases,
    label: product.label,
    labelShort: product.label,
    labelFull: product.label,
    images: product.images,
    unitPrice: product.unitPrice,
    casePack: product.casePack,
    innerCasePack: product.innerCasePack,
    metricInfo: product.metricInfo.map((info) => ({
      ...info,
      quantity: info.metric === MarketMetric.Pallets ? cases : item.quantity,
    })),
    // The server owns the rest: it prices the row, stamps it and ranks it.
    created: new Date().toISOString(),
    createdBy: Party.Buyer,
    totalPrice: '',
    caseShortName: '',
    description: '',
    hasCatalogParent: false,
    rankAttributes: [],
    rankValues: [],
    stepperIncrement: 0,
  };
};

/** A cart for a seller the server has nothing for yet. */
const createCartForSeller = (seller: MarketSellerInfo, items: CartItem[]): Cart => {
  const cases = items.reduce((total, item) => total + item.cases, 0);
  // The server ranks a cart, type and group by their newest item's created timestamp.
  const rank = Math.floor(Date.now() / 1000);

  return {
    seller,
    sellerUUID: seller.id,
    sellerName: seller.name,
    sellerOrigin: seller.origin,
    sellerMinCases: seller.moq,
    cartCases: cases,
    palletCount: String(cases),
    itemsByType: [{ label: '', rank, itemsByGroup: [{ label: '', rank, weight: 0, items }] }],
    rank,
    // A cart the server hasn't created has no identity or copy of its own yet.
    uuid: '',
    discountLabel: '',
    footer: '',
  };
};

/**
 * Puts rows the server doesn't know about yet into a cart it does, where it would put them.
 *
 * Types and groups rank by their newest item's timestamp and sort ascending, so whichever group
 * takes a new item moves to the end; within it the server sorts by the product's own ranks.
 */
const withInsertedItems = (cart: Cart, items: CartItem[]): Cart => {
  const types = cart.itemsByType.slice(0, -1);
  const lastType = cart.itemsByType[cart.itemsByType.length - 1];
  const groups = lastType.itemsByGroup.slice(0, -1);
  const lastGroup = lastType.itemsByGroup[lastType.itemsByGroup.length - 1];

  return {
    ...cart,
    itemsByType: [
      ...types,
      {
        ...lastType,
        itemsByGroup: [...groups, { ...lastGroup, items: [...lastGroup.items, ...items].sort(compareByRanks) }],
      },
    ],
  };
};

/**
 * Overlays the writes still in flight onto the carts the server returned: re-quantifying rows it
 * has, inserting the ones it doesn't, and pruning whatever a removal leaves empty.
 *
 * An insert needs the product it was added off, since the item input carries nothing to render.
 */
const withPendingItems = (carts: Cart[], pending: PendingItems): Cart[] => {
  const overlaid = new Set<string>();

  const patched = carts
    .map((cart) => ({
      ...cart,
      itemsByType: cart.itemsByType
        .map((type) => ({
          ...type,
          itemsByGroup: type.itemsByGroup
            .map((group) => ({
              ...group,
              items: group.items.flatMap((item) => {
                const key = cartItemKey(cart.seller?.id, item.productUUID);
                const write = pending[key];
                if (write === undefined) return [item];

                overlaid.add(key);
                return write.item.quantity <= 0 ? [] : [updateCartItem(item, write.item)];
              }),
            }))
            .filter((group) => group.items.length > 0),
        }))
        .filter((type) => type.itemsByGroup.length > 0),
    }))
    .filter((cart) => cart.itemsByType.length > 0);

  const inserts = new Map<string, { seller: MarketSellerInfo; items: CartItem[] }>();
  for (const [key, write] of Object.entries(pending)) {
    if (!write || write.item.quantity <= 0 || !write.product || overlaid.has(key)) continue;

    const sellerKey = String(write.sellerUUID);
    const insert = inserts.get(sellerKey) ?? { seller: write.product.seller, items: [] };
    insert.items.push(createCartItem({ item: write.item, product: write.product }));
    inserts.set(sellerKey, insert);
  }
  if (inserts.size === 0) return patched;

  const withInserts = patched.map((cart) => {
    const insert = inserts.get(String(cart.seller?.id));
    if (!insert) return cart;

    inserts.delete(String(cart.seller?.id));
    return withInsertedItems(cart, insert.items);
  });

  // Whatever is left is for a seller with no cart at all yet, which sorts after the ones that exist.
  return [...withInserts, ...[...inserts.values()].map(({ seller, items }) => createCartForSeller(seller, items))];
};

export const Context = createContext<{
  cartGroup?: CartGroup;
  carts: Cart[];
  cartItems: (cart: Cart) => CartItem[];
  cartGroupItems: CartItem[];
  deliveryDate: string;
  isFetching: boolean;
  cartsError?: CombinedError;
  hasSelectedBuyer: boolean;
  trucks?: Trucks;
  truckType?: TruckType;
  cartGroupCases: number;
  truckUsedCapacity: number;
  orderInputType: OrderInputType;
  buyerID?: string;
  canFetchCart: boolean;
  refreshSeq: number;
  checkout: (total: number, input: CartCheckoutInput) => Promise<any>;
  notify: () => Promise<undefined | CombinedError>;
  refresh: () => void;
  remove: (sellerUUID: string, productUUID: string) => void;
  findItem: (sellerUUID?: string, productUUID?: string) => CartItem | undefined;
  update: (cart: CartInput, item: CartItemInput, product?: PendingProduct) => void;
  setDeliveryDate: (value: string) => void;
  setOrderInputType: React.Dispatch<React.SetStateAction<OrderInputType>>;
} | null>(null);

export const BuyersMarketCartProvider = ({ children }: { children: ReactNode }) => {
  const { isSeller, user, isPendingReview, isRegistering, isAdmin } = useUser();
  const location = useLocation();
  const { marketInput } = useMarketInput();
  const [pendingItems, setPendingItems] = useState<PendingItems>({});
  /**
   * Writes and cart fetches are ordered against each other by counting the writes: each one takes
   * the next number as it is made and holds it for as long as it is pending. Editing a row again
   * replaces that row's pending write, so the row takes a fresh, higher number.
   */
  const writeSeq = useRef(0);
  /**
   * That count as it stood when the carts now returning were asked for: the highest numbered write
   * they can account for, since anything above it was made after their request went out.
   *
   * Say the carts hold 10 pallets of carrots and the buyer steps that row to 20, making write 5.
   * Carts asked for at 4 went out before the step and still say 10, so write 5 stays overlaid and
   * the row reads 20. Carts asked for at 5 or above went out after it, so they are the later
   * account of the row: write 5 is dropped and the row reads what they say -- 40, if a salesperson
   * moved it in the meantime.
   *
   * Numbering a fetch when it is requested rather than when it lands is what makes that second
   * case work while the step's own save is still out. Stepping 20 -> 30 before the save for 20
   * comes back renumbers the row's write to 6, and the refetch that save triggers is numbered 6
   * too, so its carts retire the write and show the 40 instead of laying 30 back over them.
   */
  const fetchedAtSeq = useRef(0);
  const { isInputsType } = useWhiteLabelling();

  const buyerID = useMemo(() => marketInput?.buyerID, [marketInput?.buyerID]);

  // Whoever has no buyer cart of their own: a seller, a user still registering or waiting on
  // review, an inputs-only tenant, a robot. Everything rooted on the cart query stays paused for
  // them, both because there is nothing to show and because a profile made seconds ago is not
  // yet readable by the resolver.
  const hasNoBuyerCart = useMemo(
    () => isSeller || isPendingReview || isRegistering || !user?.profileId || isInputsType || isHeadless(),
    [isSeller, isPendingReview, isRegistering, user?.profileId, isInputsType],
  );

  const canFetchCart = useMemo(
    () => !hasNoBuyerCart && !!isMarketStateSet(marketInput) && buyerID !== DEFAULT_ADMIN_PROFILE_ID,
    [hasNoBuyerCart, buyerID, marketInput],
  );

  // Trucks come from cart.trucks but need their own query rather than a field on the cart
  // query: that one is paused for an admin with no buyer selected (buyerID '0'), where trucks
  // are still wanted -- the resolver falls back to the admin's own profile -- so the shipping
  // dialog can offer a delivery truck before a buyer is picked.
  const [trucksResult] = useTrucksQuery({
    variables: {
      buyerID: buyerID ?? DEFAULT_ADMIN_PROFILE_ID,
    },
    pause: !buyerID || hasNoBuyerCart,
  });
  const trucks = (trucksResult.data?.cart.trucks as Trucks | undefined) ?? undefined;

  const [, notify] = useNotifyMutation();
  const { logAnalytics } = useAnalytics();

  /** Cart results */
  const [cartsResult, handleRefreshCartsResult] = useBuyersMarketCartsQuery({
    variables: {
      buyerID: buyerID ?? DEFAULT_ADMIN_PROFILE_ID,
    },
    pause: !canFetchCart,
  });

  const { data, fetching: cartResultsFetching, stale: cartsResultStale, error: cartsError } = cartsResult;
  const cartGroup = data?.cart as CartGroup;

  /**
   * The carts to render: what the server returned, with any edit still in flight overlaid.
   *
   * Deriving them means a refetch can never race an optimistic edit, and rolling one back is
   * just dropping it from the map.
   */
  const carts = useMemo(() => withPendingItems(cartGroup?.carts ?? [], pendingItems), [cartGroup?.carts, pendingItems]);

  const cartItems = useCallback(
    (cart: Cart) => cart.itemsByType.flatMap((type) => type.itemsByGroup.flatMap((group) => group.items)),
    [],
  );

  /** Every cart's items in one list, for consumers that don't care which cart they came from. */
  const cartGroupItems = useMemo(() => carts.flatMap(cartItems), [carts, cartItems]);

  /**
   * Settled carts answer every write they are numbered to account for, so those stop being pending
   * and the carts own those rows again. Writes made since the fetch went out stay overlaid.
   */
  useEffect(() => {
    if (cartResultsFetching || cartsResultStale) return;
    setPendingItems((prev) => {
      const unanswered = Object.entries(prev).filter(([, write]) => (write?.seq ?? 0) > fetchedAtSeq.current);
      return unanswered.length === Object.keys(prev).length ? prev : Object.fromEntries(unanswered);
    });
  }, [cartGroup, cartResultsFetching, cartsResultStale]);

  const [deliveryDate, setDeliveryDate] = useState(cartGroup?.deliveryDate);
  const [orderInputType, setOrderInputType] = useState(OrderInputType.Checkout);
  /** Bumped with the carts refetch, so a query kept outside this provider refetches alongside it. */
  const [refreshSeq, setRefreshSeq] = useState(0);

  const hasCartItems = useMemo(
    () => (cartGroup?.carts ?? []).some((cart) => cartItems(cart).length > 0),
    [cartGroup?.carts, cartItems],
  );

  useEffect(() => {
    const cartMatchesBuyer = cartGroup?.buyerUUID != null && String(cartGroup.buyerUUID) === String(buyerID);
    if (cartMatchesBuyer && hasCartItems && cartGroup?.deliveryDate) {
      setDeliveryDate(cartGroup.deliveryDate);
    }
  }, [cartGroup?.deliveryDate, cartGroup?.buyerUUID, hasCartItems, buyerID]);

  const cartTruckTypeUUID = cartGroup?.truckTypeUUID;

  const truckType = useMemo(
    () => trucks?.types?.find((t) => t.uuid === cartTruckTypeUUID),
    [trucks?.types, cartTruckTypeUUID],
  );

  // Prefer the locally mapped carts so optimistic quantity changes show immediately, and fall
  // back to the server totals for the window where the cart query has landed but the local
  // carts state has not been rebuilt from it yet.
  //
  // A row's cases are its own pallets rounded up, so summing them counts every part-filled row as
  // a whole pallet. Partial rows from one seller share a pallet, so each cart rounds its exact
  // total up once, as the server's cartCases does.
  const cartGroupCases = useMemo(
    () =>
      carts.length > 0
        ? carts.reduce(
            (pals, cart) =>
              pals +
              Math.ceil(
                cartItems(cart).reduce(
                  (acc, item) => acc + (item.casePack ? item.unitQuantity / item.casePack : item.cases),
                  0,
                ),
              ),
            0,
          )
        : (cartGroup?.carts?.reduce((pals, cart) => pals + cart.cartCases, 0) ?? 0),
    [carts, cartItems, cartGroup?.carts],
  );

  // The server loads a truck by each row's own pallets rounded up, so the truck counts rows, not
  // the shared pallets the badge shows.
  const truckUsedCapacity = useMemo(() => {
    const openTruckCases = trucks?.openTrucks?.find((t) => t.truckTypeUUID === cartTruckTypeUUID)?.usedCapacity ?? 0;
    const cartRowCases =
      carts.length > 0
        ? cartGroupItems.reduce((acc, item) => acc + item.cases, 0)
        : (cartGroup?.carts?.reduce((pals, cart) => pals + cart.cartCases, 0) ?? 0);
    return cartRowCases + openTruckCases;
  }, [trucks?.openTrucks, cartTruckTypeUUID, carts.length, cartGroupItems, cartGroup?.carts]);

  const [, cartSetItem] = useCartSetItemMutation();
  const [cartCheckoutResult, cartCheckout] = useCartCheckoutMutation();

  const { showSnackbar } = useSnackbar();
  const { error: checkoutError } = cartCheckoutResult;

  const isFetching = cartResultsFetching || cartsResultStale;

  useEffect(() => {
    if (buyerID) {
      setPendingItems({});
    }
  }, [buyerID]);

  // Whether this cart can name a buyer to write against. An admin who has not picked one still
  // gets a cart group back -- the BE resolves buyerID 0 to the admin's own profile -- so the
  // group's presence says nothing on its own. Once a group is loaded it is the buyer of record
  // for anything rendered from it; before then the market state store is all there is to go on.
  const hasSelectedBuyer = useMemo(
    () => (cartGroup ? isBuyerSelected(cartGroup.buyerUUID) : isBuyerSelected(buyerID)),
    [cartGroup, buyerID],
  );

  // Reset delivery date when buyer changes so it can be set from new cart or default to first loadout day
  useEffect(() => {
    setDeliveryDate('');
  }, [buyerID]);

  const refresh = useCallback(() => {
    // Numbered here, at the request, rather than when the carts arrive.
    fetchedAtSeq.current = writeSeq.current;
    setRefreshSeq((seq) => seq + 1);
    handleRefreshCartsResult();
  }, [handleRefreshCartsResult]);

  useEffect(() => {
    if (checkoutError) showSnackbar({ message: checkoutError.message }, SnackbarVariant.ERROR);
  }, [checkoutError, showSnackbar]);

  const handleNotify = useCallback(async () => {
    if (isAdmin && buyerID) {
      const result = await notify({
        input: {
          type: NotificationType.BuyerCartReady,
          IDs: [buyerID],
        },
      });
      if (result?.error) return result.error;
      refresh();
    }
  }, [isAdmin, buyerID, notify, refresh]);

  const setPendingItem = useCallback(
    (sellerUUID: string, productUUID: string, item: CartItemInput, product?: PendingProduct) => {
      writeSeq.current += 1;
      const seq = writeSeq.current;
      setPendingItems((prev) => ({
        ...prev,
        [cartItemKey(sellerUUID, productUUID)]: { sellerUUID, item, product, seq },
      }));
    },
    [],
  );

  /** Drops an edit without waiting for fresh carts: the rollback when its request fails. */
  const dropPendingItem = useCallback((sellerUUID: string, productUUID: string) => {
    setPendingItems(({ [cartItemKey(sellerUUID, productUUID)]: dropped, ...rest }) => rest);
  }, []);

  const sendCartSetItem = useCallback(
    async (cart: CartInput, item: CartItemInput) => {
      try {
        const result = await cartSetItem({ cart, item });

        if (result?.error) {
          // Undo the optimistic quantity and say why it didn't stick.
          dropPendingItem(cart.sellerUUID, item.productUUID);
          showSnackbar({ message: result.error.message }, SnackbarVariant.ERROR);
          return;
        }

        logAnalytics(item.quantity <= 0 ? 'remove_from_cart' : 'update_cart', {
          currency: 'ZAR',
          items: [{ item_id: item.productUUID, quantity: item.quantity }],
          path_location: location,
        });
        refresh();
      } catch {
        showSnackbar({ message: 'An unexpected error occured.' }, SnackbarVariant.ERROR);
        dropPendingItem(cart.sellerUUID, item.productUUID);
      }
    },
    [cartSetItem, dropPendingItem, location, logAnalytics, refresh, showSnackbar],
  );

  /**
   * The writes waiting out the debounce, keyed by the row they are for so a row's last write is
   * the only one sent: stepping a quantity down to zero replaces the quantities it passed through
   * rather than racing them. Every row waiting keeps its own entry, so one debounce covers them
   * all and clearing several rows still sends a write for each.
   */
  const unsentWrites = useRef(new Map<string, { cart: CartInput; item: CartItemInput }>());

  const sendUnsentWrites = useCallback(() => {
    const writes = [...unsentWrites.current.values()];
    unsentWrites.current.clear();

    return Promise.all(writes.map(({ cart, item }) => sendCartSetItem(cart, item)));
  }, [sendCartSetItem]);

  /** Only the requests are debounced; update applies the item to the carts on every call. */
  const debouncedSendWrites = useDebouncedFunc(sendUnsentWrites, DEBOUNCE_TIME);

  const update = useCallback(
    (cart: CartInput, item: CartItemInput, product?: PendingProduct) => {
      setPendingItem(cart.sellerUUID, item.productUUID, item, product);
      unsentWrites.current.set(cartItemKey(cart.sellerUUID, item.productUUID), { cart, item });
      debouncedSendWrites();
    },
    [debouncedSendWrites, setPendingItem],
  );

  const findItem = useCallback(
    (sellerUUID?: string, productUUID?: string) => {
      if (sellerUUID == null || productUUID == null) return undefined;
      return carts
        .filter((c) => isEqualString(c.seller?.id, sellerUUID))
        .flatMap(cartItems)
        .find((i) => isEqualString(i.productUUID, productUUID));
    },
    [carts, cartItems],
  );

  /**
   * Clearing a row is a quantity of zero, the same write the stepper makes on its way down, so a
   * row emptied from the cart and one stepped to zero take one path and share one debounce slot.
   */
  const remove = useCallback(
    (sellerId: string, productUUID: string) => {
      if (!cartGroup) return;

      const item = findItem(sellerId, productUUID);
      update(
        {
          deliveryDate: cartGroup.deliveryDate,
          buyerUUID: cartGroup.buyerUUID,
          sellerUUID: sellerId,
          shippingMethod: cartGroup.shipping?.method ?? marketInput?.method,
          truckTypeUUID: cartGroup.truckTypeUUID,
          onlyConsignment: false,
        },
        {
          productUUID,
          quantity: 0,
          ripeness: item?.ripeness ?? '',
          supply: item?.supply ?? SupplyType.Unknown,
        },
      );
    },
    [cartGroup, findItem, marketInput?.method, update],
  );

  const checkout = useCallback(
    async (total: number, input: CartCheckoutInput) => {
      // Debounced writes are still the buyer's intent: send them before the order is placed
      // against them, or a row edited moments ago checks out at its old quantity.
      await sendUnsentWrites();

      const result = await cartCheckout({
        input,
      });
      logAnalytics('purchase', {
        transaction_id: Date.now().toString(),
        currency: 'ZAR',
        value: total,
        items: carts,
      });
      refresh();
      return result;
    },
    [refresh, cartCheckout, carts, sendUnsentWrites, logAnalytics],
  );

  const value = useMemo(
    () => ({
      cartGroup,
      carts,
      cartItems,
      cartGroupItems,
      deliveryDate,
      trucks,
      truckType,
      cartGroupCases,
      truckUsedCapacity,
      hasSelectedBuyer,
      isFetching,
      cartsError,
      orderInputType,
      buyerID,
      canFetchCart,
      refreshSeq,
      checkout,
      notify: handleNotify,
      update,
      findItem,
      remove,
      refresh,
      setDeliveryDate,
      setOrderInputType,
    }),
    [
      cartGroup,
      carts,
      cartItems,
      cartGroupItems,
      deliveryDate,
      isFetching,
      cartsError,
      hasSelectedBuyer,
      orderInputType,
      buyerID,
      canFetchCart,
      refreshSeq,
      trucks,
      truckType,
      cartGroupCases,
      truckUsedCapacity,
      checkout,
      handleNotify,
      update,
      refresh,
      findItem,
      remove,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useBuyersMarketCart = () => {
  const result = useContext(Context);
  if (!result) throw new Error('useBuyersMarketCart must be used within a BuyersCartProvider');
  return result;
};
